import { axios } from '@ben/api/axios';
import { PaymentSourceEnum } from '@ben/shared/types/benefit';
import { type IModularBenefitDetail } from '@ben/shared/types/columbia';
import type { BenLanguageBackendCode } from '@ben/common/languages';
import { BEN_LANGUAGES } from '@ben/common/languages';
const createEmptyTranslationFields = <T extends string>(
  field: T
): Record<`${T}${BenLanguageBackendCode}`, string> => {
  return BEN_LANGUAGES.reduce(
    (accumulator, { backendCode }) => {
      return { ...accumulator, [`${field}${backendCode}`]: '' };
    },
    {} as Record<`${T}${BenLanguageBackendCode}`, string>
  );
};

export const emptyBenefit: IModularBenefitDetail = {
  type: 'MODULAR',
  ...createEmptyTranslationFields('name'),
  ...createEmptyTranslationFields('displayName'),
  rank: undefined,
  employer: '',
  benefitProgramme: '',
  ...createEmptyTranslationFields('content'),
  ...createEmptyTranslationFields('shortDescription'),
  externalLinkUrl: '',
  optOutReasonsGroup: undefined,
  ...createEmptyTranslationFields('ctaText'),
  enrolmentType: '',
  benefitRequirements: [],
  categories: [],
  showPrice: false,
  paymentSource: PaymentSourceEnum.SALARY,
  ageCalculateFrom: 'TODAY',
  salaryCalculateFrom: 'TODAY',
  deductionType: undefined,
  managedBy: undefined,
  provider: undefined,
  groupMembershipNumber: undefined,
  additionalConfig: {
    id: undefined,
    steps: [],
    subPrices: [],
    rateTables: [],
  },
  closeOptInWindowAfterOptOut: undefined,
  closeWindowsOnFulfillment: false,
  hideClosedConfigWindowBanner: false,
  benefitWindows: [],
  startDate: '',
  renewalDate: '',
  activationDelay: '',
  changeDelay: '',
  deactivationDelay: '',
  closeConfigWindowAfterOptIn: false,
  keepEnrolledUntilSpa: false,
  statePensionAgeConfig: null,
  cancelEnrolmentOnOptOutDuringOptInWindow: false,
  autoActivating: false,
  requiresActivationApproval: false,
  requiresDeactivationApproval: false,
  includeInNmwCalculation: false,
  deactivateEnrolmentsWithProgramme: true,
  eligibilityRules: [],
  optInButtonText: '',
  clientBenefitCustomCode: '',
  optOutReasonsGroupType: undefined,
  broker: '',
  showChanges: false,
  showEnrolments: false,
  showPaymentHistory: false,
  prorateForPayroll: true,
  remainderAttribution: 'FIRST_CYCLE',
  postCutoffBehaviour: 'ADD_TO_NEXT_CYCLE',
  pensionChangeField: 'AMOUNT',
  newBenefitType: 'REGULAR',
  enableRequests: false,
  status: 'DRAFT',
  payDeductionType: '',
  recalculatePriceAfterEmployeeChange: false,
  recalculatePriceAfterEmployeeSalaryChange: false,
  renewalConfig: {
    scheduledRenewalDate: '',
    renewalWindowStartDate: '',
    renewalWindowEndDate: '',
    hasConfigWindow: false,
    hasOptInWindow: false,
    hasOptOutWindow: false,
  },
};

export const getModularBenefitById = async (
  benefitId?: number | string,
  versionId?: string
) => {
  const versionSuffix = versionId ? `versions/${versionId}/` : '';
  const response =
    typeof window === 'undefined' || benefitId === 'create'
      ? { data: emptyBenefit }
      : await axios.get<IModularBenefitDetail>(
          `benefits/${benefitId}/${versionSuffix}`
        );
  const benefit = response?.data;
  const additionalConfig = benefit.additionalConfig ?? {
    id: undefined,
    steps: [],
    subPrices: [],
    rateTables: [],
  };
  return { ...benefit, additionalConfig: additionalConfig };
};
